import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useSetRecoilState } from 'recoil';
import { useHistory, useLocation } from 'react-router';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import {
  FolderAPIAgentModel,
  FolderChatAgentModel,
  FolderCreationRequest,
  FolderModel,
  FolderProjectModel,
  FolderToolkitModel,
  FolderUpdatingRequest,
} from '../../../../gamma-api';
import { defaultApi } from '../../../apis';
import { ValidationErrors } from '../../../types';
import { getPageFromSearchParams, PAGE_INDEX_PARAM } from '../../../utils/urlUtil';
import { getTranslateFunc } from '../../../utils/stringUtil';
import { tryGetValidationErrors } from '../../../utils/errorUtils';
import { getApiAgentPath, getChatAgentPath, getProjectPath, getToolkitPath } from '../../../utils/pathUtils';
import { alertsSelectorAdd, selectedFolderIdSelector } from '../../../recoil';
import IbIcon from '../../../components/common/IbIcon';
import IbTypography from '../../../components/common/IbTypography';
import IbButton, { IIbButtonAction } from '../../../components/common/IbButton';
import IbPageTitle from '../../../components/IbPageTitle';
import IbPagination from '../../../components/common/IbPagination';
import IbTable from '../../../components/common/IbTable';
import IbContextMenu, { IIbContextMenuItem } from '../../../components/common/IbContextMenu';
import IbTextHighlighter from '../../../components/common/IbTextHighlighter';
import Avatar, { AvatarName } from '../../../components/common/Avatar';
import IbTooltip from '../../../components/common/IbTooltip';
import ImportUpload from '../../../components/ImportUpload';
import ExportButton from '../../../components/ExportButton';

import {
  CONTENT_CLASS_NAME,
  COPY_BUTTON_CLASS_NAME,
  FOLDERS_PAGE_SIZE,
  ID_CELL_CLASS_NAME,
  MAIN_CLASS_NAME,
  NAME_CELL_CLASS_NAME,
  PAGINATION_CLASS_NAME,
  TOOLBAR_CLASS_NAME,
  TOOLTIP_MOUSE_DELAY,
  TRANSLATION_GROUP_PREFIX,
} from './const';
import FolderModal from './FolderModal';
import ConfirmModal from './ConfirmModal';
import { isDeletableFolder, isExportableFolder } from './utils';
import FolderItems from './FolderItems';

const FoldersPage: React.FC = () => {
  const location = useLocation();
  const { push } = useHistory();

  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const setFolderId = useSetRecoilState(selectedFolderIdSelector);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: FOLDERS_PAGE_SIZE,
    total: 0,
  });

  const [searchText, setSearchText] = useState('');
  const [folders, setFolders] = useState<FolderModel[]>([]);

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const [folderModalVisible, setFolderModalVisible] = useState(false);

  const [contextFolder, setContextFolder] = useState<FolderModel>();
  const [modalFolder, setModalFolder] = useState<FolderModel>();
  const [deleteFolder, setDeleteFolder] = useState<FolderModel>();

  const translate = getTranslateFunc(TRANSLATION_GROUP_PREFIX);

  const loadFolders = async (withLoading: boolean) => {
    withLoading && setLoading(true);

    try {
      const response = await defaultApi.searchFolders(
        (pagination.current || 1) - 1,
        FOLDERS_PAGE_SIZE,
        true,
        searchText
      );
      setFolders(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    withLoading && setLoading(false);
  };

  useEffect(() => {
    loadFolders(true).finally();
  }, [pagination.current, pagination.pageSize, searchText]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== pagination.current) {
      setPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [searchText]);

  useUpdateEffect(() => {
    if (pagination.current) {
      const params = new URLSearchParams(location.search);
      params.set(PAGE_INDEX_PARAM, pagination.current.toString());
      push({ pathname: location.pathname, search: params.toString() });
    }
  }, [pagination.current]);

  const resetFolderModal = () => {
    setFolderModalVisible(false);
    setModalFolder(undefined);
  };

  const onCreateFolderClick = () => {
    setFolderModalVisible(true);
  };

  const onImportFileFinish = async () => {
    await loadFolders(false);
  };

  const onFolderModalCancel = () => {
    resetFolderModal();
  };

  const onFolderCreate = async (
    request: FolderCreationRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    try {
      await defaultApi.createFolder(request);
      await loadFolders(false);
      resetFolderModal();
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: translate('Failed to create'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onFolderUpdate = async (
    request: FolderUpdatingRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    if (!modalFolder) return;

    try {
      await defaultApi.updateFolder(modalFolder.folder_id, request);
      await loadFolders(false);
      resetFolderModal();
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: translate('Failed to update'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!deleteFolder) {
      return;
    }

    setDeleting(true);

    try {
      await defaultApi.deleteFolder(deleteFolder.folder_id);
      setFolderId(undefined);
    } catch (e) {
      addAlert({
        content: translate('Failed to delete'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      // eslint-disable-next-line no-console
      console.error(e);
    }

    await loadFolders(false);
    setDeleting(false);
    setDeleteFolder(undefined);
  };

  const onDeleteCancelButtonClick = () => {
    setDeleteFolder(undefined);
  };

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onSearchTextChange = (value: string) => setSearchText(value);

  const renderChatAgentItem = (folder: FolderModel) => (item: unknown) => {
    const agent = item as FolderChatAgentModel;
    const title = agent.name || '-';

    return (
      <IbTooltip key={agent.agent_id} mouseEnterDelay={TOOLTIP_MOUSE_DELAY} placement="left" title={title}>
        <Link to={getChatAgentPath(folder.folder_id, agent.agent_id)}>
          <Avatar avatarName={agent.avatar as AvatarName} size={32} />
          <IbTypography>
            <IbTypography.Paragraph type="secondary">{title}</IbTypography.Paragraph>
          </IbTypography>
        </Link>
      </IbTooltip>
    );
  };

  const renderApiAgentItem = (folder: FolderModel) => (item: unknown) => {
    const agent = item as FolderAPIAgentModel;
    const title = agent.name || '-';

    return (
      <IbTooltip key={agent.agent_id} mouseEnterDelay={TOOLTIP_MOUSE_DELAY} placement="left" title={title}>
        <Link to={getApiAgentPath(folder.folder_id, agent.agent_id)}>
          <IbTypography>
            <IbTypography.Paragraph type="secondary">{title}</IbTypography.Paragraph>
          </IbTypography>
        </Link>
      </IbTooltip>
    );
  };

  const renderProjectItem = (folder: FolderModel) => (item: unknown) => {
    const project = item as FolderProjectModel;
    const title = project.name || '-';

    return (
      <IbTooltip key={project.project_id} mouseEnterDelay={TOOLTIP_MOUSE_DELAY} placement="left" title={title}>
        <Link to={getProjectPath(folder.folder_id, project.project_id)}>
          <IbTypography>
            <IbTypography.Paragraph type="secondary">{title}</IbTypography.Paragraph>
          </IbTypography>
        </Link>
      </IbTooltip>
    );
  };

  const renderToolkitItem = (folder: FolderModel) => (item: unknown) => {
    const toolkit = item as FolderToolkitModel;
    const title = toolkit.name || '-';

    return (
      <IbTooltip key={toolkit.toolkit_id} mouseEnterDelay={TOOLTIP_MOUSE_DELAY} placement="left" title={title}>
        <Link to={getToolkitPath(folder.folder_id, toolkit.toolkit_id)}>
          <IbTypography>
            <IbTypography.Paragraph type="secondary">{title}</IbTypography.Paragraph>
          </IbTypography>
        </Link>
      </IbTooltip>
    );
  };

  const renderNameCell = (folder: FolderModel) => {
    const title = folder.name;

    return (
      <div className={NAME_CELL_CLASS_NAME}>
        <IbTooltip mouseEnterDelay={TOOLTIP_MOUSE_DELAY} title={title}>
          <IbTypography>
            <IbTypography.Paragraph type="secondary">
              <IbTextHighlighter highlightedText={searchText} text={title} />
            </IbTypography.Paragraph>
          </IbTypography>
        </IbTooltip>
      </div>
    );
  };

  const renderIdCell = (folder: FolderModel) => {
    const onCopyButtonClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      navigator.clipboard.writeText(folder.external_id).finally();
    };

    return (
      <div className={ID_CELL_CLASS_NAME}>
        <IbTooltip mouseEnterDelay={TOOLTIP_MOUSE_DELAY} title={folder.external_id}>
          <IbTypography>
            <IbTypography.Paragraph type="secondary">{folder.external_id}</IbTypography.Paragraph>
          </IbTypography>
        </IbTooltip>
        <IbTooltip mouseEnterDelay={TOOLTIP_MOUSE_DELAY} title={translate('Button: Copy: Tooltip')}>
          <IbButton
            className={COPY_BUTTON_CLASS_NAME}
            icon={<IbIcon iconName="copy-one" />}
            type="link"
            onClick={onCopyButtonClick}
          />
        </IbTooltip>
      </div>
    );
  };

  const renderChatAgentsCell = (folder: FolderModel) => {
    return <FolderItems group="Chat agents" items={folder.chat_agents} onRender={renderChatAgentItem(folder)} />;
  };

  const renderApiAgentsCell = (folder: FolderModel) => {
    return <FolderItems group="API agents" items={folder.api_agents} onRender={renderApiAgentItem(folder)} />;
  };

  const renderProjectsCell = (folder: FolderModel) => {
    return <FolderItems group="Projects" items={folder.projects} onRender={renderProjectItem(folder)} />;
  };

  const renderToolkitsCell = (folder: FolderModel) => {
    return <FolderItems group="Toolkits" items={folder.toolkits} onRender={renderToolkitItem(folder)} />;
  };

  const renderContextMenu = (folder: FolderModel) => {
    const onUpdateClick = () => {
      setModalFolder(folder);
      setFolderModalVisible(true);
    };

    const onDeleteClick = () => setDeleteFolder(folder);

    const onMenuOpen = () => setContextFolder(folder);

    const onMenuClose = () => setContextFolder(undefined);

    const menuItems: IIbContextMenuItem[] = [];

    if (isExportableFolder(folder)) {
      menuItems.push({
        icon: <IbIcon iconName="efferent-three" />,
        text: translate('Context: Export'),
        render: (child) => <ExportButton.FolderExport folderId={folder.folder_id}>{child}</ExportButton.FolderExport>,
      });
    }

    menuItems.push({
      icon: <IbIcon iconName="afferent-three" />,
      text: translate('Context: Import'),
      render: (child) => (
        <ImportUpload.FolderImport folderId={folder.folder_id} onFinish={onImportFileFinish}>
          {child}
        </ImportUpload.FolderImport>
      ),
    });

    menuItems.push({
      icon: <IbIcon iconName="edit" />,
      text: translate('Context: Update'),
      onSelect: onUpdateClick,
    });

    if (isDeletableFolder(folder)) {
      menuItems.push({
        icon: <IbIcon iconName="delete" />,
        text: translate('Context: Delete'),
        onSelect: onDeleteClick,
      });
    }

    return (
      <IbContextMenu
        menuItems={menuItems}
        visible={contextFolder?.folder_id === folder.folder_id}
        onClose={onMenuClose}
      >
        <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onMenuOpen}></IbButton>
      </IbContextMenu>
    );
  };

  const renderToolbar = () => {
    const actions: IIbButtonAction[] = [
      {
        icon: <IbIcon iconName="afferent-three" />,
        text: translate('Toolbar: Import'),
        render: (child) => <ImportUpload.FolderImport onFinish={onImportFileFinish}>{child}</ImportUpload.FolderImport>,
      },
    ];

    return (
      <div className={TOOLBAR_CLASS_NAME}>
        <IbButton actions={actions} icon={<IbIcon iconName="add-one" />} onClick={onCreateFolderClick}>
          {translate('Toolbar: Create')}
        </IbButton>
      </div>
    );
  };

  const renderTable = () => {
    const columns: ColumnType<FolderModel>[] = [
      {
        title: translate('Table: Name'),
        width: '16%',
        render: (_, folder) => renderNameCell(folder),
      },
      {
        title: translate('Table: ID'),
        width: '16%',
        render: (_, folder) => renderIdCell(folder),
      },
      {
        title: translate('Table: Chat agents'),
        width: '17%',
        render: (_, folder) => renderChatAgentsCell(folder),
      },
      {
        title: translate('Table: API agents'),
        width: '17%',
        render: (_, folder) => renderApiAgentsCell(folder),
      },
      {
        title: translate('Table: Projects'),
        width: '17%',
        render: (_, folder) => renderProjectsCell(folder),
      },
      {
        title: translate('Table: Toolkits'),
        width: '17%',
        render: (_, folder) => renderToolkitsCell(folder),
      },
      {
        title: '',
        render: (_, folder) => renderContextMenu(folder),
      },
    ];

    return (
      <IbTable
        columns={columns}
        dataSource={folders}
        emptyText={<IbTypography.Paragraph disabled>{translate('Nothing found')}</IbTypography.Paragraph>}
        loading={loading}
        rowKey={(folder: FolderModel) => folder.folder_id}
      />
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPageTitle
        showSearchInput
        searchPlaceholder={translate('Search: Placeholder')}
        title={translate('Title')}
        onDebouncedSearch={onSearchTextChange}
      />
      <div className={CONTENT_CLASS_NAME}>
        {renderToolbar()}
        {renderTable()}
      </div>
      {(pagination.total ?? 0) > FOLDERS_PAGE_SIZE && (
        <div className={PAGINATION_CLASS_NAME}>
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={FOLDERS_PAGE_SIZE}
            pageSize={FOLDERS_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
      <FolderModal
        editFolder={modalFolder}
        visible={folderModalVisible}
        onCancel={onFolderModalCancel}
        onCreate={onFolderCreate}
        onUpdate={onFolderUpdate}
      />
      <ConfirmModal.DeleteFolder
        folder={deleteFolder}
        loading={deleting}
        onCancel={onDeleteCancelButtonClick}
        onConfirm={onDeleteConfirmButtonClick}
      />
    </div>
  );
};

export default FoldersPage;
